import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Calendar, Percent, ArrowDownToLine } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { FinancialChart } from '../components/Chart';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface PaymentSchedule {
  paymentNumber: number;
  paymentAmount: number;
  principal: number;
  interest: number;
  remainingBalance: number;
  totalInterest: number;
}

const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState<number>(250000);
  const [interestRate, setInterestRate] = useState<number>(4.5);
  const [loanTerm, setLoanTerm] = useState<number>(30);
  const [paymentFrequency, setPaymentFrequency] = useState<'monthly' | 'biweekly'>('monthly');
  const [schedule, setSchedule] = useState<PaymentSchedule[]>([]);

  const calculateLoan = () => {
    const periodicRate = (interestRate / 100) / 12;
    const numberOfPayments = loanTerm * (paymentFrequency === 'monthly' ? 12 : 26);
    const paymentAmount = loanAmount * (periodicRate * Math.pow(1 + periodicRate, numberOfPayments)) / (Math.pow(1 + periodicRate, numberOfPayments) - 1);
    
    let balance = loanAmount;
    let totalInterestPaid = 0;
    let newSchedule: PaymentSchedule[] = [];

    for (let i = 1; i <= numberOfPayments; i++) {
      const interestPayment = balance * periodicRate;
      const principalPayment = paymentAmount - interestPayment;
      
      totalInterestPaid += interestPayment;
      balance -= principalPayment;

      newSchedule.push({
        paymentNumber: i,
        paymentAmount: paymentAmount,
        principal: principalPayment,
        interest: interestPayment,
        remainingBalance: Math.max(0, balance),
        totalInterest: totalInterestPaid
      });
    }

    setSchedule(newSchedule);
  };

  useEffect(() => {
    calculateLoan();
  }, [loanAmount, interestRate, loanTerm, paymentFrequency]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Loan Amortization Schedule', 14, 22);
    
    doc.setFontSize(12);
    doc.text(`Loan Amount: $${loanAmount.toLocaleString()}`, 14, 32);
    doc.text(`Interest Rate: ${interestRate}%`, 14, 40);
    doc.text(`Loan Term: ${loanTerm} years`, 14, 48);
    doc.text(`Payment Frequency: ${paymentFrequency}`, 14, 56);
    
    const tableData = schedule.map(row => [
      row.paymentNumber,
      `$${row.paymentAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${row.principal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${row.interest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${row.remainingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${row.totalInterest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ]);
    
    (doc as any).autoTable({
      head: [['#', 'Payment', 'Principal', 'Interest', 'Balance', 'Total Interest']],
      body: tableData,
      startY: 65,
      theme: 'grid',
      styles: { fontSize: 8 }
    });
    
    doc.save('loan-amortization.pdf');
  };

  const chartData = {
    labels: schedule.slice(0, 12).map(row => `Payment ${row.paymentNumber}`),
    datasets: [
      {
        label: 'Principal',
        data: schedule.slice(0, 12).map(row => row.principal),
        backgroundColor: '#10b981',
      },
      {
        label: 'Interest',
        data: schedule.slice(0, 12).map(row => row.interest),
        backgroundColor: '#34d399',
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />

        <div className="mb-8">
          <Newsletter 
            title="Master Your Loan Strategy"
            subtitle="Get expert tips on loan management, refinancing opportunities, and debt reduction strategies."
          />
        </div>
        
        <div className="glass-card p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <Calculator className="w-6 h-6 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-semibold">Loan Amortization Calculator</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Loan Amount</label>
              <div className="relative">
                <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Interest Rate (%)</label>
              <div className="relative">
                <Percent className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                  max="30"
                  step="0.1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Loan Term (Years)</label>
              <div className="relative">
                <Calendar className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="1"
                  max="50"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Payment Frequency</label>
              <select
                value={paymentFrequency}
                onChange={(e) => setPaymentFrequency(e.target.value as 'monthly' | 'biweekly')}
                className="glass-input px-4 py-2 w-full rounded-lg"
              >
                <option value="monthly">Monthly</option>
                <option value="biweekly">Bi-weekly</option>
              </select>
            </div>
          </div>

          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
          >
            <ArrowDownToLine className="w-4 h-4" />
            Export Schedule
          </button>
        </div>

        <div className="glass-card p-8 mb-8">
          <h3 className="text-xl font-semibold mb-6">First Year Payment Breakdown</h3>
          <FinancialChart
            type="bar"
            data={chartData}
            height={400}
            className="mb-8"
          />
        </div>

        <div className="glass-card p-8">
          <h3 className="text-xl font-semibold mb-6">Amortization Schedule</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-3">#</th>
                  <th className="text-right p-3">Payment</th>
                  <th className="text-right p-3">Principal</th>
                  <th className="text-right p-3">Interest</th>
                  <th className="text-right p-3">Remaining Balance</th>
                  <th className="text-right p-3">Total Interest</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, index) => (
                  <tr key={index} className="border-b border-slate-800 hover:bg-slate-800/30">
                    <td className="p-3">{row.paymentNumber}</td>
                    <td className="text-right p-3">
                      ${row.paymentAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-right p-3">
                      ${row.principal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-right p-3">
                      ${row.interest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-right p-3">
                      ${row.remainingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-right p-3">
                      ${row.totalInterest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8">
          <Newsletter 
            title="Optimize Your Loan Payments"
            subtitle="Subscribe for personalized loan management strategies and money-saving tips."
          />
        </div>
      </div>
    </div>
  );
};

export default LoanCalculator;